import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Input,
  Button,
  HStack,
  VStack,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Skeleton,
  InputGroup,
  InputRightElement,
  useToast,
} from "@chakra-ui/react";
import { FiEdit, FiSearch, FiX, FiCheck } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { useGetEmployees } from "../../services/employeeService";
import { useGetAdminEmployees } from "../../services/companyService";
import {
  useGetEmployeeBreakSettings,
  useUpdateEmployeeBreakSettings,
} from "../../services/configurationService";
import ModalComponent from "../../components/modal";
import AvatarComponent from "../../components/avatar";

const EmployeeBreakSettingsTable = () => {
  const toast = useToast();
  const { user } = useUserContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [customHours, setCustomHours] = useState("");
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);

  const isCompanyAdmin = user?.role === "company_admin";

  // Fetch employees based on user role
  const adminEmployeesData = useGetAdminEmployees({
    enabled: isCompanyAdmin,
  });
  const regularEmployeesData = useGetEmployees({
    enabled: !isCompanyAdmin,
  });

  const getEmployeesIsLoading = isCompanyAdmin
    ? adminEmployeesData.adminEmployeesIsLoading
    : regularEmployeesData.getEmployeesIsLoading;

  const employeesData = isCompanyAdmin
    ? adminEmployeesData.adminEmployees?.data?.employees || []
    : regularEmployeesData.getEmployeesData?.employees || regularEmployeesData.getEmployeesData || [];

  // Refetch employees after updating break settings
  const refetchEmployees = isCompanyAdmin
    ? adminEmployeesData.refetchAdminEmployees
    : regularEmployeesData.refetchGetEmployees;

  // Fetch break settings for selected employee
  const {
    employeeBreakSettingsData,
    employeeBreakSettingsIsLoading,
    refetchEmployeeBreakSettings,
  } = useGetEmployeeBreakSettings({
    employeeId: selectedEmployee?.employee_id,
    enabled: !!selectedEmployee?.employee_id && isOpen,
  });

  const { updateEmployeeBreakSettingsIsLoading, updateEmployeeBreakSettingsPayload } =
    useUpdateEmployeeBreakSettings((res) => {
      refetchEmployeeBreakSettings();
      refetchEmployees(); // Refetch employee list to get updated break settings
      onClose();
      setSelectedEmployee(null);
      setCustomHours("");
      toast({
        title: "Success",
        description: "Employee break settings updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    });

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!employeesData || !Array.isArray(employeesData)) return [];
    
    return employeesData.filter((emp) => {
      const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
      const employeeId = (emp.employee_id || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      const search = searchTerm.toLowerCase();
      
      return fullName.includes(search) || employeeId.includes(search) || email.includes(search);
    });
  }, [employeesData, searchTerm]);

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setCustomHours("");
    setEditingEmployeeId(employee.employee_id);
    onOpen();
  };

  const handleSave = () => {
    if (!selectedEmployee) return;

    const hours = customHours.trim() === "" ? null : parseFloat(customHours);
    
    if (customHours.trim() !== "" && (isNaN(hours) || hours < 0 || hours > 24)) {
      toast({
        title: "Validation Error",
        description: "Break hours must be a number between 0 and 24",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    updateEmployeeBreakSettingsPayload({
      employee_id: selectedEmployee.employee_id,
      allowed_break_hours: hours,
    });
  };

  const handleRemoveOverride = () => {
    if (!selectedEmployee) return;
    
    updateEmployeeBreakSettingsPayload({
      employee_id: selectedEmployee.employee_id,
      allowed_break_hours: null,
    });
  };

  // Initialize custom hours when employee break settings are loaded
  React.useEffect(() => {
    if (employeeBreakSettingsData && isOpen) {
      if (employeeBreakSettingsData.has_override) {
        setCustomHours(employeeBreakSettingsData.override_break_hours?.toString() || "");
      } else {
        setCustomHours("");
      }
    }
  }, [employeeBreakSettingsData, isOpen]);

  if (getEmployeesIsLoading) {
    return (
      <Box p={6}>
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Employee ID</Th>
                <Th>Default Break Hours</Th>
                <Th>Custom Break Hours</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <Tr key={i}>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>
      <VStack spacing={4} align="stretch">
        {/* Search Bar */}
        <Box>
          <InputGroup>
            <Input
              placeholder="Search by name, ID, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="md"
            />
            <InputRightElement>
              <FiSearch color="gray.400" />
            </InputRightElement>
          </InputGroup>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Employee ID</Th>
                <Th>Email</Th>
                <Th>Break Hours</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredEmployees.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <VStack spacing={2}>
                      <Text color="gray.500">No employees found</Text>
                      {searchTerm && (
                        <Text fontSize="sm" color="gray.400">
                          Try adjusting your search term
                        </Text>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              ) : (
                filteredEmployees.map((employee) => {
                  const breakHours = employee.allowed_break_hours ?? employee.default_break_hours ?? "N/A";
                  const hasOverride = employee.has_break_override ?? false;
                  
                  return (
                    <Tr key={employee.employee_id} _hover={{ bg: "gray.50" }}>
                      <Td>
                        <HStack spacing={3}>
                          <AvatarComponent
                            name={`${employee.first_name} ${employee.last_name}`}
                            image={employee.profile_image}
                          />
                          <Text fontWeight="500">
                            {employee.first_name} {employee.last_name}
                          </Text>
                        </HStack>
                      </Td>
                      <Td>{employee.employee_id}</Td>
                      <Td>{employee.email || "N/A"}</Td>
                      <Td>
                        <Text fontWeight="500" color="gray.700">
                          {typeof breakHours === 'number' ? `${breakHours} hrs` : breakHours}
                        </Text>
                      </Td>
                      <Td>
                        {hasOverride ? (
                          <Badge colorScheme="blue">Custom</Badge>
                        ) : (
                          <Badge colorScheme="gray">Default</Badge>
                        )}
                      </Td>
                      <Td>
                        <IconButton
                          icon={<FiEdit />}
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(employee)}
                          aria-label="Edit break settings"
                        />
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </VStack>

      {/* Edit Modal */}
      <ModalComponent
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title={`Break Settings - ${selectedEmployee ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}` : ""}`}
        closeBtn={true}
        closeModal={onClose}
      >
        {employeeBreakSettingsIsLoading ? (
          <Box p={6} textAlign="center">
            <Skeleton height="100px" />
          </Box>
        ) : (
          <VStack spacing={4} align="stretch">
            <Box p={4} bg="gray.50" borderRadius="8px">
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Default Break Hours (Company/Branch)
              </Text>
              <Text fontSize="lg" color="blue.600" fontWeight="600" mt={1}>
                {employeeBreakSettingsData?.default_break_hours ?? selectedEmployee?.default_break_hours ?? "N/A"} hours
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="500" color="gray.700" mb={2}>
                Custom Break Hours (Override)
              </Text>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                placeholder={(employeeBreakSettingsData?.default_break_hours ?? selectedEmployee?.default_break_hours ?? 1.0).toString()}
                size="md"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Leave empty to use default. Enter a value to set custom break hours.
              </Text>
            </Box>

            <HStack spacing={3} justify="flex-end">
              {(employeeBreakSettingsData?.has_override ?? selectedEmployee?.has_break_override) && (
                <Button
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<FiX />}
                  onClick={handleRemoveOverride}
                  isLoading={updateEmployeeBreakSettingsIsLoading}
                  size="md"
                >
                  Remove Override
                </Button>
              )}
              <Button
                colorScheme="blue"
                leftIcon={<FiCheck />}
                onClick={handleSave}
                isLoading={updateEmployeeBreakSettingsIsLoading}
                size="md"
              >
                Save
              </Button>
            </HStack>

            {(employeeBreakSettingsData?.has_override ?? selectedEmployee?.has_break_override) && (
              <Box p={3} bg="blue.50" borderRadius="8px" border="1px solid" borderColor="blue.200">
                <HStack spacing={2}>
                  <Badge colorScheme="blue">Override Active</Badge>
                  <Text fontSize="sm" color="gray.700">
                    Current: {employeeBreakSettingsData?.allowed_break_hours ?? selectedEmployee?.allowed_break_hours ?? "N/A"} hours
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        )}
      </ModalComponent>
    </>
  );
};

export default EmployeeBreakSettingsTable;

